import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Loader2, CheckCircle2, XCircle, UserCheck, AlertTriangle, GitMerge, Search, Download, ShieldCheck, Clock, KeyRound, Image as ImageIcon, Pencil, Camera, Save, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import LoginStatus from "./LoginStatus";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STANDARDS } from "@/lib/standards";

const fmtIST = (iso) => {
  if (!iso) return "—";
  return formatDateTime(iso) || "—";
};
const csvCell = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
function downloadCSV(filename, headers, rows) {
  const lines = [headers.map((h) => csvCell(h.label)).join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => csvCell(h.get(r))).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}
function Field({ label, a, b }) {
  const diff = (a || "") !== (b || "") && b;
  return (
    <div className="grid grid-cols-3 gap-2 text-sm py-1 border-b border-slate-100">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-700 break-words">{a || "—"}</span>
      <span className={`break-words ${diff ? "text-amber-700 font-semibold" : "text-slate-700"}`}>{b || "—"}{diff ? " (change)" : ""}</span>
    </div>
  );
}

// ======================= APPROVE / REVIEW DIALOG =======================
function ApproveDialog({ row, onClose, onDone }) {
  const r = row.request, s = row.existing || {};
  const [f, setF] = useState({
    name: r.submitted_name || s.name || "",
    student_class: r.admin_class || s.student_class || "",
    date_of_birth: r.submitted_dob || s.date_of_birth || "",
    parent_name: r.submitted_parent_name || s.parent_name || "",
    parent_contact: r.submitted_parent_contact || s.parent_contact || "",
    parent_contact_alt: r.submitted_parent_contact_alt || s.parent_contact_alt || "",
    guardian_email: r.guardian_email || s.guardian_email || "",
  });
  const [confirmed, setConfirmed] = useState(false);
  const [method, setMethod] = useState("call");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const saveOnly = async () => {
    setBusy("save");
    try { await api.post(`/admin/activation-requests/${r.id}/save`, { corrections: f });
      toast.success("Corrections saved (not activated)"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };
  const approve = async () => {
    if (!confirmed) { toast.error("Confirm the parent's identity first"); return; }
    if (!note.trim()) { toast.error("Add a confirmation note"); return; }
    setBusy("approve");
    try { await api.post(`/admin/activation-requests/${r.id}/approve`, { parent_confirmed: true, confirmation_method: method, confirmation_note: note.trim(), corrections: f });
      toast.success("Account approved — login enabled"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); } finally { setBusy(""); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="approve-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="h-5 w-5" /> Review & approve · Roll {r.roll_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs">
            <div className="grid grid-cols-3 gap-2 text-slate-400 mb-1"><span>Field</span><span>Imported</span><span>Submitted</span></div>
            <Field label="Name" a={s.name} b={r.submitted_name} />
            <Field label="Class" a={s.student_class} b={r.submitted_class || r.admin_class} />
            <Field label="Date of birth" a={s.date_of_birth} b={r.submitted_dob} />
            <Field label="Parent name" a={s.parent_name} b={r.submitted_parent_name} />
            <Field label="Parent contact" a={s.parent_contact} b={r.submitted_parent_contact} />
            <Field label="Email" a={s.guardian_email} b={r.guardian_email} />
            {r.class_correction_note && <div className="pt-1.5 text-amber-700">Correction request: {r.class_correction_note}</div>}
          </div>
          <p className="text-sm font-semibold text-slate-700">Final details (editable)</p>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={set("name")} data-testid="appr-name" /></div>
            <div><Label className="text-xs">Class / Std</Label><Input value={f.student_class} onChange={set("student_class")} data-testid="appr-class" /></div>
            <div><Label className="text-xs">Date of birth</Label><Input type="date" value={f.date_of_birth} onChange={set("date_of_birth")} data-testid="appr-dob" /></div>
            <div><Label className="text-xs">Parent name</Label><Input value={f.parent_name} onChange={set("parent_name")} data-testid="appr-parent" /></div>
            <div><Label className="text-xs">Parent contact</Label><Input value={f.parent_contact} onChange={set("parent_contact")} placeholder="+91…" data-testid="appr-contact" /></div>
            <div><Label className="text-xs">Additional contact</Label><Input value={f.parent_contact_alt} onChange={set("parent_contact_alt")} placeholder="optional" data-testid="appr-contact-alt" /></div>
            <div className="col-span-2"><Label className="text-xs">Email (optional)</Label><Input value={f.guardian_email} onChange={set("guardian_email")} placeholder="optional" data-testid="appr-email" /></div>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="appr-confirmed" /> Parent identity manually confirmed
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600">How?</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="text-sm border border-slate-300 rounded-lg p-1.5" data-testid="appr-method">
                <option value="call">Phone call to existing parent number</option>
                <option value="in_person">In person</option>
              </select>
            </div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Confirmation note (required)" data-testid="appr-note" />
            <p className="text-[11px] text-slate-500">Manual confirmation does not mark the email as verified.</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={saveOnly} disabled={!!busy} data-testid="appr-save">
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Pencil className="h-4 w-4 mr-1" /> Save corrections</>}
          </Button>
          <Button onClick={approve} disabled={!!busy} className="bg-green-600 hover:bg-green-700" data-testid="appr-approve">
            {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Save Corrections & Approve Account</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ======================= ONBOARDING LIST =======================
function OnboardingSection({ rows, onApprove, onReject, onIssueTemp, busy }) {
  const [filter, setFilter] = useState("pending_admin");
  const [q, setQ] = useState("");
  const list = useMemo(() => rows.filter((r) => r.status === filter).filter((r) => {
    const s = q.trim().toLowerCase();
    return !s || [r.roll, r.name, r.parentEmail].some((v) => (v || "").toLowerCase().includes(s));
  }), [rows, filter, q]);
  const counts = {
    pending_admin: rows.filter((r) => r.status === "pending_admin").length,
    approved: rows.filter((r) => r.status === "approved").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
    pending_email: rows.filter((r) => r.status === "pending_email").length,
  };
  const TABS = [
    ["pending_admin", `Awaiting approval (${counts.pending_admin})`],
    ["approved", `Activated (${counts.approved})`],
    ["rejected", `Rejected (${counts.rejected})`],
    ["pending_email", `Not completed (${counts.pending_email})`],
  ];
  return (
    <div className="space-y-3" data-testid="onboarding-section">
      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${filter === k ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`onb-filter-${k}`}>{label}</button>
        ))}
      </div>
      <div className="relative max-w-sm">
        <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roll, name, email" className="pl-8" data-testid="onb-search" />
      </div>
      {list.length === 0 ? <p className="text-sm text-slate-500 py-4" data-testid="onb-empty">No records in this list.</p> : (
        <div className="space-y-3">
          {list.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`onb-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <span className="font-bold text-slate-900">Roll {r.roll || "—"}</span>
                {r.status === "pending_admin" && <Badge className="bg-blue-700">Awaiting approval</Badge>}
                {r.status === "approved" && <Badge variant="success"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Activated</Badge>}
                {r.status === "rejected" && <Badge variant="destructive">Rejected</Badge>}
                {r.status === "pending_email" && <Badge variant="warning"><Clock className="h-3.5 w-3.5 mr-1" /> Not completed</Badge>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                <div><div className="text-[11px] uppercase text-slate-400">Name</div>{r.name || "—"}</div>
                <div><div className="text-[11px] uppercase text-slate-400">Class</div>{r.klass || "—"}</div>
                <div><div className="text-[11px] uppercase text-slate-400">Parent contact</div>{r.request?.submitted_parent_contact || "—"}</div>
                <div><div className="text-[11px] uppercase text-slate-400">Email</div>{r.parentEmail || "—"}</div>
                <div><div className="text-[11px] uppercase text-slate-400">Submitted</div>{fmtIST(r.request?.updated_at || r.requestAt)}</div>
                {r.status === "approved" && <div><div className="text-[11px] uppercase text-slate-400">Confirmed by</div>{r.request?.parent_confirmed_method || "—"}</div>}
                {r.status === "rejected" && r.note && <div className="col-span-2"><div className="text-[11px] uppercase text-slate-400">Reason</div>{r.note}</div>}
              </div>
              {r.status === "pending_admin" && (
                <div className="flex flex-wrap gap-2 pt-3">
                  <Button size="sm" onClick={() => onApprove(r)} className="bg-green-600 hover:bg-green-700" data-testid={`onb-review-${r.id}`}><UserCheck className="h-4 w-4 mr-1" /> Review / Edit & Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => onReject(r.id)} disabled={busy === r.id} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`onb-reject-${r.id}`}><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
                </div>
              )}
              {r.status === "pending_email" && (
                <div className="flex flex-col gap-1.5 pt-3">
                  <p className="text-xs text-slate-500">If the student can still use their original password they can simply log in to start onboarding (no email verification needed). If they can't, issue a temporary password after confirming identity:</p>
                  <div><Button size="sm" variant="outline" onClick={() => onIssueTemp(r)} data-testid={`onb-temp-${r.id}`}><KeyRound className="h-4 w-4 mr-1" /> Issue temporary password</Button></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ======================= TEMP PASSWORDS =======================
function TempPasswordTool() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});
  const [days, setDays] = useState(14);
  const [q, setQ] = useState("");
  const [klass, setKlass] = useState("");
  const [teacher, setTeacher] = useState("");
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/onboarding/unactivated"); setRows(data); }
    catch { toast.error("Could not load students"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const classes = Array.from(new Set(rows.map((r) => r.student_class).filter(Boolean))).sort();
  const teachers = Array.from(new Set(rows.map((r) => r.teacher).filter(Boolean))).sort();
  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    if (s && ![r.roll_number, r.name, r.teacher].some((v) => (v || "").toLowerCase().includes(s))) return false;
    if (klass && r.student_class !== klass) return false;
    if (teacher && r.teacher !== teacher) return false;
    return true;
  });
  const selIds = Object.keys(sel).filter((id) => sel[id]);

  const issueFor = async (ids, label) => {
    if (ids.length === 0) { toast.error("No students to issue for"); return; }
    if (!window.confirm(`Generate temporary passwords for ${ids.length} ${label}? This invalidates any previous temporary password for them.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/students/issue-temp-passwords", { student_ids: ids, valid_days: Number(days) || 14 });
      setIssued(data); setSel({});
      toast.success(`Issued ${data.issued.length}${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate"); } finally { setBusy(false); }
  };
  const generate = () => issueFor(selIds, "selected student(s)");
  const generateFiltered = () => issueFor(filtered.map((r) => r.id), "student(s) in this view");
  const removeTemp = async (ids) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Delete the temporary password for ${ids.length} student(s)? It will stop working and they'll be removed from this list. Their own password (if any) is not affected.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/students/remove-temp-passwords", { student_ids: ids }); toast.success(`Removed ${data.removed}`); setSel({}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); } finally { setBusy(false); }
  };

  const downloadIssued = () => {
    if (!issued?.issued?.length) return;
    const rows = [...issued.issued].sort((a, b) => (a.teacher || "zzz").localeCompare(b.teacher || "zzz"));
    downloadCSV(`ptg_temp_passwords_${new Date().toISOString().slice(0, 10)}.csv`,
      [{ label: "Assigned Teacher", get: (r) => r.teacher || "Unassigned" }, { label: "Student Name", get: (r) => r.name },
       { label: "Std", get: (r) => r.student_class }, { label: "Roll Number", get: (r) => r.roll_number },
       { label: "Temporary Password", get: (r) => r.temp_password }, { label: "Expires", get: (r) => fmtIST(r.expires_at) }], rows);
  };

  return (
    <div className="space-y-3" data-testid="temp-pw-tool">
      <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900">
        Generate a <b>unique, secure</b> temporary password per selected unactivated student. Passwords grant onboarding access only (not fees/stars/attendance), are stored hashed, and the plaintext is shown <b>only once</b> below — download and distribute privately. Already-activated students are never reset.
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[160px]"><Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roll, name, teacher" className="pl-8" data-testid="temp-search" /></div>
        <div><Label className="text-xs">Class / Std</Label>
          <select value={klass} onChange={(e) => setKlass(e.target.value)} className="h-10 text-sm border border-slate-300 rounded-lg px-2 w-36" data-testid="temp-filter-class">
            <option value="">All classes</option>{classes.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
        <div><Label className="text-xs">Teacher</Label>
          <select value={teacher} onChange={(e) => setTeacher(e.target.value)} className="h-10 text-sm border border-slate-300 rounded-lg px-2 w-44" data-testid="temp-filter-teacher">
            <option value="">All teachers</option>{teachers.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
        <div><Label className="text-xs">Valid for (days)</Label><Input type="number" min="1" max="60" value={days} onChange={(e) => setDays(e.target.value)} className="w-24" data-testid="temp-days" /></div>
        {(klass || teacher || q) && <Button variant="ghost" size="sm" onClick={() => { setKlass(""); setTeacher(""); setQ(""); }} data-testid="temp-clear">Clear</Button>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={generateFiltered} disabled={busy || filtered.length === 0} className="bg-blue-900 hover:bg-blue-950" data-testid="temp-generate-filtered">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1" /> Issue for all {filtered.length} in this view</>}
        </Button>
        <Button onClick={generate} disabled={busy || selIds.length === 0} variant="outline" data-testid="temp-generate">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Issue for {selIds.length} selected</>}
        </Button>
        <Button onClick={() => removeTemp(selIds)} disabled={busy || selIds.length === 0} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="temp-remove-selected"><Trash2 className="h-4 w-4 mr-1" /> Delete {selIds.length} selected</Button>
        <span className="text-xs text-slate-500">Tip: filter by class or teacher, then issue for the whole batch in one click.</span>
      </div>

      {issued && (
        <div className="rounded-xl border border-green-300 bg-green-50 p-3" data-testid="temp-issued">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-green-800">{issued.issued.length} temporary password(s) issued — shown once</p>
            <Button size="sm" variant="outline" onClick={downloadIssued} data-testid="temp-download"><Download className="h-4 w-4 mr-1" /> Download teacher-wise CSV</Button>
          </div>
          <div className="max-h-52 overflow-y-auto text-xs">
            <table className="w-full"><thead><tr className="text-slate-500 text-left"><th className="p-1">Teacher</th><th>Name</th><th>Std</th><th>Roll</th><th>Temp password</th><th>Expires</th></tr></thead>
              <tbody>{issued.issued.map((r) => (<tr key={r.id} className="border-t border-green-200"><td className="p-1">{r.teacher || "—"}</td><td>{r.name}</td><td>{r.student_class}</td><td>{r.roll_number}</td><td className="font-mono font-bold">{r.temp_password}</td><td>{fmtIST(r.expires_at)}</td></tr>))}</tbody></table>
          </div>
          {issued.skipped.length > 0 && <p className="text-xs text-amber-700 mt-2">Skipped: {issued.skipped.map((s) => `${s.roll_number || s.id} (${s.reason})`).join(", ")}</p>}
        </div>
      )}

      {loading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-blue-900" /></div> : (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500 text-left sticky top-0"><tr>
              <th className="p-2"><input type="checkbox" checked={selIds.length === filtered.length && filtered.length > 0} onChange={(e) => { const m = {}; if (e.target.checked) filtered.forEach((r) => (m[r.id] = true)); setSel(m); }} data-testid="temp-select-all" /></th>
              <th>Roll</th><th>Name</th><th>Std</th><th>Teacher</th><th>Status</th><th></th></tr></thead>
              <tbody>{filtered.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`temp-row-${r.id}`}>
                  <td className="p-2"><input type="checkbox" checked={!!sel[r.id]} onChange={(e) => setSel((m) => ({ ...m, [r.id]: e.target.checked }))} data-testid={`temp-select-${r.id}`} /></td>
                  <td className="font-semibold">{r.roll_number}</td><td>{r.name}</td><td>{r.student_class || "—"}</td><td className="text-slate-500">{r.teacher || "—"}</td>
                  <td>{r.has_submitted ? <Badge className="bg-blue-700 text-[10px]">Submitted</Badge> : r.temp_active ? <Badge variant="warning" className="text-[10px]">Temp active</Badge> : <span className="text-slate-400 text-xs">Not started</span>}{r.activated && <Badge className="bg-green-600 text-[10px] ml-1">Activated</Badge>}</td>
                  <td className="pr-2 text-right"><Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" disabled={busy} onClick={() => removeTemp([r.id])} title="Delete temporary password" data-testid={`temp-remove-${r.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>))}</tbody></table>
          </div>
          <div className="p-2 text-xs text-slate-500 bg-slate-50">{filtered.length} student(s) with a temporary password or not yet activated</div>
        </div>
      )}
    </div>
  );
}

// ======================= PROFILE CHANGE REQUESTS =======================
const FIELD_LABELS = { name: "Full name", student_class: "Std / class", date_of_birth: "Date of birth", parent_name: "Parent/guardian name", parent_contact: "Parent contact", parent_contact_alt: "Additional contact", email: "Email" };

function ProfileReqCard({ item, onDone }) {
  const r = item.request, s = item.student || {};
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState(r.fields || {});
  const [busy, setBusy] = useState("");
  const [teacherIds, setTeacherIds] = useState(item.current_teacher_ids || []);
  const classChange = "student_class" in fields;
  const setF = (k) => (e) => setFields((x) => ({ ...x, [k]: e.target.value }));

  const saveEdit = async () => {
    setBusy("edit");
    try { const { data } = await api.post(`/admin/profile-requests/${r.id}/edit`, { fields });
      setFields(data.fields); setEditing(false); toast.success("Proposed changes updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };
  const doApprove = async () => {
    setBusy("approve");
    const body = { fields };
    if (classChange) body.teacher_ids = teacherIds;
    try { await api.post(`/admin/profile-requests/${r.id}/approve`, body);
      toast.success("Applied to the student record"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); } finally { setBusy(""); }
  };
  const reject = async () => {
    const reason = window.prompt("Reason for rejecting? (optional)") ?? null;
    if (reason === null) return;
    setBusy("reject");
    try { await api.post(`/admin/profile-requests/${r.id}/reject`, { note: reason }); toast.success("Rejected"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const toggleTeacher = (id) => setTeacherIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`profreq-${r.id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="font-bold">Roll {r.roll_number} · {s?.name}</span>
        <div className="flex gap-2 items-center">
          {classChange && <Badge variant="warning">Class change</Badge>}
          <Badge className="bg-blue-700">Pending</Badge>
        </div>
      </div>

      {!editing ? (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs">
          <div className="grid grid-cols-3 gap-2 text-slate-400 mb-1"><span>Field</span><span>Current</span><span>Proposed</span></div>
          {Object.entries(fields).map(([k, v]) => (
            <Field key={k} label={FIELD_LABELS[k] || k} a={k === "email" ? s?.guardian_email : s?.[k]} b={v} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2" data-testid={`profreq-edit-${r.id}`}>
          {Object.keys(fields).map((k) => (
            <div key={k} className={k === "name" || k === "email" ? "col-span-2" : ""}>
              <Label className="text-xs">{FIELD_LABELS[k] || k}</Label>
              {k === "student_class" ? (
                <Select value={fields[k] || undefined} onValueChange={(v) => setFields((x) => ({ ...x, student_class: v }))}>
                  <SelectTrigger data-testid={`profreq-edit-class-${r.id}`}><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>{STANDARDS.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={k === "date_of_birth" ? "date" : "text"} value={fields[k] || ""} onChange={setF(k)} data-testid={`profreq-edit-${k}-${r.id}`} />
              )}
            </div>
          ))}
        </div>
      )}

      {classChange && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid={`profreq-teachers-${r.id}`}>
          <p className="text-xs font-semibold text-amber-800 mb-1">Class change affects teacher assignments. Currently: {item.current_teachers || "none"}. Select who teaches this student after the change:</p>
          <div className="flex flex-wrap gap-2">
            {(item.all_teachers || []).map((t) => (
              <label key={t.id} className={`text-xs px-2 py-1 rounded-full border cursor-pointer ${teacherIds.includes(t.id) ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300"}`} data-testid={`profreq-teacher-${r.id}-${t.id}`}>
                <input type="checkbox" className="hidden" checked={teacherIds.includes(t.id)} onChange={() => toggleTeacher(t.id)} /> {t.name}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Fees, balances, payments, attendance and stars are not changed.</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-3">
        {editing ? (
          <>
            <Button size="sm" onClick={saveEdit} disabled={busy === "edit"} data-testid={`profreq-save-${r.id}`}>{busy === "edit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save edits</>}</Button>
            <Button size="sm" variant="outline" onClick={() => { setFields(r.fields || {}); setEditing(false); }}>Cancel edit</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`profreq-edit-btn-${r.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
        )}
        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={doApprove} disabled={!!busy || editing} data-testid={`profreq-approve-${r.id}`}>
          {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</>}
        </Button>
        <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={reject} disabled={!!busy} data-testid={`profreq-reject-${r.id}`}><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
      </div>
    </div>
  );
}

function ProfileRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/profile-requests"); setRows(data); } catch { toast.error("Could not load"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-blue-900" /></div>;
  if (rows.length === 0) return <p className="text-sm text-slate-500 py-4" data-testid="profreq-empty">No pending profile changes.</p>;
  return (
    <div className="space-y-3" data-testid="profile-requests">
      {rows.map((item) => <ProfileReqCard key={item.request.id} item={item} onDone={load} />)}
    </div>
  );
}

// ======================= PHOTO REQUESTS =======================
function PhotoRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const fileRefs = useRef({});

  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/photo-requests"); setRows(data); } catch { toast.error("Could not load"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const act = async (sid, action) => {
    setBusy(sid + action);
    try { await api.post(`/admin/students/${sid}/photo/${action}`); toast.success(action === "approve" ? "Photo approved" : "Photo rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const replace = async (sid, file) => {
    if (!file) return;
    setBusy(sid + "upload");
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/students/${sid}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo set on behalf of student"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-blue-900" /></div>;
  if (rows.length === 0) return <p className="text-sm text-slate-500 py-4" data-testid="photoreq-empty">No photos awaiting approval.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="photo-requests">
      {rows.map((r) => (
        <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`photoreq-${r.id}`}>
          <div className="flex items-center justify-between mb-2"><span className="font-bold">Roll {r.roll_number} · {r.name}</span><Badge variant="warning">Pending</Badge></div>
          <div className="flex gap-3">
            <div className="text-center"><div className="text-[11px] text-slate-400 mb-1">Current</div>
              {r.has_current ? <AuthImage path={`/student-photo/${r.id}`} className="h-24 w-24 object-cover rounded-lg border" /> : <div className="h-24 w-24 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300"><ImageIcon className="h-8 w-8" /></div>}</div>
            <div className="text-center"><div className="text-[11px] text-amber-600 mb-1">New (pending)</div>
              <AuthImage path={`/student-photo/${r.id}?pending=1`} className="h-24 w-24 object-cover rounded-lg border border-amber-300" /></div>
          </div>
          <div className="flex flex-wrap gap-2 pt-3">
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => act(r.id, "approve")} disabled={!!busy} data-testid={`photoreq-approve-${r.id}`}>{busy === r.id + "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve"}</Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => act(r.id, "reject")} disabled={!!busy} data-testid={`photoreq-reject-${r.id}`}>Reject</Button>
            <input type="file" accept="image/*" className="hidden" ref={(el) => (fileRefs.current[r.id] = el)} onChange={(e) => replace(r.id, e.target.files?.[0])} />
            <Button size="sm" variant="outline" onClick={() => fileRefs.current[r.id]?.click()} disabled={!!busy} data-testid={`photoreq-replace-${r.id}`}><Camera className="h-4 w-4 mr-1" /> Upload on behalf</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ======================= MERGE DIALOG (duplicate handling, unchanged logic) =======================
function MergeDialog({ group, onClose, onDone }) {
  const [primaryId, setPrimaryId] = useState(() => { const noLogin = group.records.filter((r) => !r.has_login); return (noLogin[0] || group.records[0]).id; });
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [carryLogin, setCarryLogin] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const secondaryIds = group.records.map((r) => r.id).filter((id) => id !== primaryId);
  const runPreview = useCallback(async () => {
    setLoading(true); setPreview(null); setOverrides({}); setCarryLogin("");
    try { const { data } = await api.post("/admin/students/merge/preview", { primary_id: primaryId, secondary_ids: secondaryIds }); setPreview(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not build preview"); } finally { setLoading(false); }
  }, [primaryId]); // eslint-disable-line
  useEffect(() => { runPreview(); }, [runPreview]);
  const apply = async () => {
    if (!window.confirm("Merge these duplicates into the primary? Duplicates are archived (recoverable), not deleted.")) return;
    setSaving(true);
    try { const { data } = await api.post("/admin/students/merge", { primary_id: primaryId, secondary_ids: secondaryIds, overrides, carry_login_from: carryLogin || null });
      const moved = Object.values(data.moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`Merged. ${moved} linked records + ${data.attendance_relinked} attendance entries moved.`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); } finally { setSaving(false); }
  };
  const linked = preview?.linked_records || {};
  const linkedRows = Object.entries(linked).filter(([, v]) => v > 0);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GitMerge className="h-5 w-5" /> Merge duplicate roll {group.roll_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><p className="text-sm font-semibold text-slate-700 mb-1">Keep as primary</p>
            <div className="space-y-2">{group.records.map((r) => (
              <label key={r.id} className={`flex items-start gap-2 rounded-lg border p-2 cursor-pointer ${primaryId === r.id ? "border-blue-600 bg-blue-50" : "border-slate-200"}`} data-testid={`merge-primary-${r.id}`}>
                <input type="radio" name="primary" checked={primaryId === r.id} onChange={() => setPrimaryId(r.id)} className="mt-1" />
                <div className="text-sm"><div className="font-semibold text-slate-800">{r.name || "—"} {r.has_login && <Badge className="ml-1 bg-slate-600 text-[10px]">has login</Badge>}</div>
                  <div className="text-xs text-slate-500">{r.student_class || "—"} · {r.guardian_email || "no email"} · ⭐ {r.star_balance}</div></div>
              </label>))}</div></div>
          {loading && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-blue-900" /></div>}
          {preview && (<>
            <div><p className="text-sm font-semibold text-slate-700 mb-1">Data moving to primary</p>
              <div className="rounded-lg bg-slate-50 border p-2 text-xs text-slate-600 space-y-0.5" data-testid="merge-linked">
                {linkedRows.length === 0 && preview.attendance_docs === 0 && <div>No linked records.</div>}
                {linkedRows.map(([k, v]) => <div key={k}>• {v} × {k.replace(/_/g, " ")}</div>)}
                {preview.attendance_docs > 0 && <div>• {preview.attendance_docs} × attendance day record(s)</div>}
                {preview.star_balance_to_add > 0 && <div>• ⭐ {preview.star_balance_to_add} stars added</div>}</div></div>
            {preview.conflicts.length > 0 && (<div><p className="text-sm font-semibold text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Conflicts</p>
              <div className="space-y-2">{preview.conflicts.map((c) => (
                <div key={c.secondary_id + c.field} className="text-xs border border-amber-200 rounded-lg p-2 bg-amber-50" data-testid={`merge-conflict-${c.field}`}>
                  <div className="font-semibold text-slate-700">{c.label}</div>
                  <label className="flex items-center gap-1 mt-1"><input type="radio" name={`ov-${c.field}`} checked={(overrides[c.field] ?? c.primary_value) === c.primary_value} onChange={() => setOverrides((o) => ({ ...o, [c.field]: c.primary_value }))} /> Keep: <b>{c.primary_value || "—"}</b></label>
                  <label className="flex items-center gap-1"><input type="radio" name={`ov-${c.field}`} checked={overrides[c.field] === c.secondary_value} onChange={() => setOverrides((o) => ({ ...o, [c.field]: c.secondary_value }))} /> Use: <b>{c.secondary_value}</b></label>
                </div>))}</div></div>)}
          </>)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="merge-cancel">Cancel</Button>
          <Button onClick={apply} disabled={saving || loading || !preview} className="bg-blue-900 hover:bg-blue-950" data-testid="merge-apply">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GitMerge className="h-4 w-4 mr-1" /> Merge & archive</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ======================= ISSUE TEMP PASSWORD FOR A "NOT COMPLETED" REQUEST =======================
function TempForRequestDialog({ row, onClose }) {
  const [method, setMethod] = useState("call");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const issue = async () => {
    if (!note.trim()) { toast.error("Add a confirmation note"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/activation-requests/${row.id}/issue-temp-password`, { confirmation_method: method, confirmation_note: note.trim() });
      setResult(data);
      toast.success("Temporary password issued — shown once");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not issue"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="temp-req-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Issue temporary password · Roll {row.roll}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-2" data-testid="temp-req-result">
            <div className="rounded-xl border border-green-300 bg-green-50 p-3 text-sm">
              <p className="text-green-800 font-semibold mb-1">Give this privately to {result.name} ({result.roll_number}):</p>
              <p className="font-mono text-lg font-bold" data-testid="temp-req-pw">{result.temp_password}</p>
              <p className="text-xs text-slate-500 mt-1">Std {result.student_class || "—"} · Teacher {result.teacher || "—"} · Expires {fmtIST(result.expires_at)}</p>
              <p className="text-[11px] text-slate-500 mt-1">Shown once and stored hashed. The student logs in with this to begin onboarding, then sets their own password.</p>
            </div>
            <DialogFooter><Button onClick={onClose} data-testid="temp-req-done">Done</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Confirm the student/parent identity before issuing. This replaces any previous temporary password and does not grant full access.</p>
            <div className="flex items-center gap-2"><span className="text-xs text-slate-600">How confirmed?</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="text-sm border border-slate-300 rounded-lg p-1.5" data-testid="temp-req-method">
                <option value="call">Phone call to existing parent number</option><option value="in_person">In person</option></select></div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Confirmation note (required)" data-testid="temp-req-note" />
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-blue-900 hover:bg-blue-950" onClick={issue} disabled={busy} data-testid="temp-req-issue">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Issue temporary password"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ======================= PASSWORD RESET (activated students, no email) =======================
function PasswordResetTool() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [reqs, setReqs] = useState([]);
  const loadReqs = useCallback(() => api.get("/admin/password-reset-requests").then(({ data }) => setReqs(data)).catch(() => {}), []);
  useEffect(() => { loadReqs(); }, [loadReqs]);
  const dismiss = async (id) => { try { await api.post(`/admin/password-reset-requests/${id}/dismiss`); loadReqs(); } catch { toast.error("Could not dismiss"); } };

  const search = async () => {
    if (!q.trim()) { toast.error("Enter a roll number or name"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/admin/students");
      const s = q.trim().toLowerCase();
      setRows(data.filter((r) => [r.roll_number, r.name].some((v) => (v || "").toLowerCase().includes(s))).slice(0, 25));
    } catch { toast.error("Could not search students"); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid="password-reset-tool">
      <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900">
        For an <b>already-activated</b> student who forgot their password and has no usable email. Issue a new temporary password (shown once); the student logs in and must immediately set their own. Fees, stars and records stay intact.
      </div>
      {reqs.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="pwreset-requests">
          <p className="text-sm font-bold text-amber-900">Requests from teachers ({reqs.length})</p>
          {reqs.map((r) => (
            <div key={r.id} className="rounded-lg bg-white border border-amber-100 p-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`pwreset-req-${r.id}`}>
              <span className="text-sm"><b>Roll {r.roll_number}</b> · {r.name} · {r.student_class || "—"} <span className="text-xs text-slate-500">· asked by {r.teacher_name} · {formatDateTime(r.created_at)}</span></span>
              <div className="flex gap-2">
                <Button size="sm" className="bg-blue-900 hover:bg-blue-950" onClick={() => setTarget({ id: r.student_id, roll_number: r.roll_number, name: r.name })} data-testid={`pwreset-req-reset-${r.id}`}><KeyRound className="h-4 w-4 mr-1" /> Reset</Button>
                <Button size="sm" variant="outline" onClick={() => dismiss(r.id)} data-testid={`pwreset-req-dismiss-${r.id}`}>Dismiss</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <div className="relative flex-1"><Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search by roll number or name" className="pl-8" data-testid="pwreset-search" /></div>
        <Button onClick={search} disabled={loading} className="bg-blue-900 hover:bg-blue-950" data-testid="pwreset-search-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find"}</Button>
      </div>
      {rows && rows.length === 0 && <p className="text-sm text-slate-500" data-testid="pwreset-empty">No students match.</p>}
      {rows && rows.map((s) => (
        <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2" data-testid={`pwreset-row-${s.id}`}>
          <div className="text-sm"><span className="font-bold">Roll {s.roll_number}</span> · {s.name} · {s.student_class || "—"}
            <span className="ml-2">{s.status === "approved" ? <Badge variant="success" className="text-[10px]">Activated</Badge> : <Badge variant="warning" className="text-[10px]">Not activated</Badge>}</span></div>
          {s.status === "approved"
            ? <Button size="sm" variant="outline" onClick={() => setTarget(s)} data-testid={`pwreset-open-${s.id}`}><KeyRound className="h-4 w-4 mr-1" /> Reset password</Button>
            : <span className="text-xs text-slate-400">Use Onboarding → temp password</span>}
        </div>
      ))}
      {target && <ResetDialog student={target} onClose={() => { setTarget(null); loadReqs(); }} />}
    </div>
  );
}

function ResetDialog({ student, onClose }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState("");
  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/students/${student.id}/reset-password`, {});
      setResult(data); toast.success("New temporary password issued — shown once");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset"); } finally { setBusy(false); }
  };
  const sendToTeacher = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/messaging/password-handoff", { student_id: student.id, password: result.temp_password });
      const names = (data.notified || []).join(", ");
      setSentMsg(`Sent privately to ${names} in Messages.`);
      toast.success(`Sent to ${names}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send to teacher"); } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="pwreset-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Reset password · Roll {student.roll_number}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-2" data-testid="pwreset-result">
            <div className="rounded-xl border border-green-300 bg-green-50 p-3 text-sm">
              <p className="text-green-800 font-semibold mb-1">Give this privately to {result.name} ({result.roll_number}):</p>
              <p className="font-mono text-lg font-bold" data-testid="pwreset-pw">{result.temp_password}</p>
              <p className="text-xs text-slate-500 mt-1">Std {result.student_class || "—"} · Teacher {result.teacher || "—"}. They log in with this and must set their own password immediately.</p>
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
              <p className="text-xs text-slate-600 mb-2">Prefer the class teacher to forward it? Send these details privately to the assigned teacher(s) via Messages (students can't see that thread).</p>
              {sentMsg ? (
                <p className="text-sm font-semibold text-green-700 flex items-center gap-1.5" data-testid="pwreset-sent-msg"><CheckCircle2 className="h-4 w-4" /> {sentMsg}</p>
              ) : (
                <Button size="sm" variant="outline" className="border-blue-300 text-blue-800" onClick={sendToTeacher} disabled={sending} data-testid="pwreset-send-teacher">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />} Send to class teacher
                </Button>
              )}
            </div>
            <DialogFooter><Button onClick={onClose} data-testid="pwreset-done">Done</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">This replaces their current password with a new one, shown once.</p>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-blue-900 hover:bg-blue-950" onClick={issue} disabled={busy} data-testid="pwreset-issue">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ======================= MAIN =======================
export default function AdminActivations({ onChange }) {
  const [rows, setRows] = useState([]);
  const [dups, setDups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [mergeGroup, setMergeGroup] = useState(null);
  const [approveRow, setApproveRow] = useState(null);
  const [tempReqRow, setTempReqRow] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, d] = await Promise.all([api.get("/admin/activation-requests"), api.get("/admin/duplicate-students")]);
      const flat = a.data.map(({ request: r, existing: s }) => ({
        id: r.id, status: r.status, roll: r.roll_number,
        name: s?.name || r.submitted_name, klass: s?.student_class || r.submitted_class || r.admin_class,
        parentEmail: r.guardian_email, requestAt: r.created_at, note: r.admin_note,
        request: r, existing: s,
      }));
      setRows(flat); setDups(d.data); onChange && onChange();
    } catch { toast.error("Could not load activation requests"); } finally { setLoading(false); }
  }, [onChange]);
  useEffect(() => { load(); }, [load]);

  const reject = async (rid) => {
    const note = window.prompt("Reason for rejection (the student can correct and resubmit):") || "";
    setBusy(rid);
    try { await api.post(`/admin/activation-requests/${rid}/reject`, { note }); toast.success("Request rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-6" data-testid="admin-activations">
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><UserCheck className="h-5 w-5" /> Onboarding & Activations</h2>
        <p className="text-sm text-slate-500">See who has set their own password, who still needs to, and who has stopped logging in. New student approvals stay in Star System → Pending. Passwords are never shown.</p>
      </div>

      <Tabs defaultValue="onboarding">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="onboarding" data-testid="tab-onboarding"><UserCheck className="h-4 w-4 mr-1" /> Login status</TabsTrigger>
          <TabsTrigger value="temp" data-testid="tab-temp"><KeyRound className="h-4 w-4 mr-1" /> Temp Passwords</TabsTrigger>
          <TabsTrigger value="reset" data-testid="tab-reset"><KeyRound className="h-4 w-4 mr-1" /> Password Reset</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="tab-profiles"><Pencil className="h-4 w-4 mr-1" /> Profile Changes</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos"><ImageIcon className="h-4 w-4 mr-1" /> Photos</TabsTrigger>
          <TabsTrigger value="dups" data-testid="tab-dups"><AlertTriangle className="h-4 w-4 mr-1" /> Duplicates ({dups.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="onboarding" className="mt-4"><LoginStatus /></TabsContent>
        <TabsContent value="temp" className="mt-4"><TempPasswordTool /></TabsContent>
        <TabsContent value="reset" className="mt-4"><PasswordResetTool /></TabsContent>
        <TabsContent value="profiles" className="mt-4"><ProfileRequests /></TabsContent>
        <TabsContent value="photos" className="mt-4"><PhotoRequests /></TabsContent>
        <TabsContent value="dups" className="mt-4">
          {dups.length === 0 && <p className="text-slate-500 text-sm" data-testid="dups-empty">No duplicate roll numbers found.</p>}
          {dups.map((g) => (
            <div key={g.roll_key} className="rounded-xl border border-amber-200 bg-amber-50 p-3 mt-2 text-sm" data-testid={`dup-${g.roll_key}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap"><b>Roll {g.roll_number}</b> <span className="text-slate-500">— {g.count} records</span>
                <Button size="sm" onClick={() => setMergeGroup(g)} className="bg-blue-900 hover:bg-blue-950 ml-auto" data-testid={`merge-open-${g.roll_key}`}><GitMerge className="h-4 w-4 mr-1" /> Review & merge</Button></div>
              {g.records.map((rec) => (<div key={rec.id} className="text-xs text-slate-600 mt-1">• {rec.name} · {rec.student_class || "—"} · {rec.has_login ? "has login" : "no login"} · ⭐ {rec.star_balance}</div>))}
            </div>))}
        </TabsContent>
      </Tabs>

      {approveRow && <ApproveDialog row={approveRow} onClose={() => setApproveRow(null)} onDone={() => { setApproveRow(null); load(); }} />}
      {tempReqRow && <TempForRequestDialog row={tempReqRow} onClose={() => { setTempReqRow(null); load(); }} />}
      {mergeGroup && <MergeDialog group={mergeGroup} onClose={() => setMergeGroup(null)} onDone={() => { setMergeGroup(null); load(); }} />}
    </div>
  );
}
